import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '@utils/constants';

/**
 * Base API service with axios instance configuration
 * Handles authentication, error handling, and request/response interceptors
 */

interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
  detail?: string;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds
    });

    this.setup_interceptors();
  }

  /**
   * Setup request and response interceptors
   */
private setup_interceptors(): void {
  // Request interceptor - Add auth token
  this.api.interceptors.request.use(
    (config) => {
      const token = this.get_auth_token();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle errors globally with blob support
  this.api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original_request = error.config as AxiosRequestConfig & { _retry?: boolean };

      // IMPORTANT: Handle blob errors before other processing
      if (original_request.responseType === 'blob' && error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          try {
            const json = JSON.parse(text);
            error.response.data = json; // Replace blob with parsed JSON
          } catch {
            error.response.data = { error: text };
          }
        } catch (blobError) {
          console.error('Failed to parse blob error:', blobError);
        }
      }

      // Handle 401 Unauthorized - Token expired
      if (error.response?.status === 401 && !original_request._retry) {
        original_request._retry = true;

        try {
          const refresh_token = this.get_refresh_token();
          if (refresh_token) {
            const response = await this.refresh_auth_token(refresh_token);
            this.set_auth_token(response.access);
            
            // Retry original request with new token
            if (original_request.headers) {
              original_request.headers.Authorization = `Bearer ${response.access}`;
            }
            return this.api(original_request);
          }
        } catch (refresh_error) {
          // Refresh failed - logout user
          this.clear_auth_tokens();
          window.location.href = '/login';
          return Promise.reject(refresh_error);
        }
      }

      return Promise.reject(this.normalize_error(error));
    }
  );
}
  /**
   * Normalize error response for consistent error handling
   * Handles Django REST Framework error formats
   */
  private normalize_error(error: AxiosError): ApiError {
    // Add detailed logging for debugging
    console.log('=== API ERROR DEBUG ===');
    console.log('Error:', error);
    console.log('Response:', error.response);
    console.log('Response data:', error.response?.data);
    console.log('Response status:', error.response?.status);
    console.log('======================');

    if (error.response) {
      // Server responded with error status
      const data = error.response.data as any;
      const status = error.response.status;
      
      let message = 'An error occurred';
      let errors: Record<string, string[]> | undefined;
      let detail: string | undefined;

      // Case 1: Django REST Framework validation errors
      // Format: { "field_name": ["error1", "error2"], "another_field": ["error"] }
      if (data && typeof data === 'object') {
        // Check if this looks like DRF field validation errors
        const hasFieldErrors = Object.keys(data).some(key => 
          Array.isArray(data[key]) && 
          data[key].every((item: any) => typeof item === 'string')
        );

        if (hasFieldErrors && !data.detail && !data.message) {
          // This is DRF field validation errors
          errors = {};
          
          for (const [field, fieldErrors] of Object.entries(data)) {
            if (Array.isArray(fieldErrors)) {
              errors[field] = fieldErrors as string[];
            }
          }

          // Create a human-readable message from the first error
          const firstField = Object.keys(errors)[0];
          const firstError = errors[firstField]?.[0];
          
          if (firstField === 'non_field_errors') {
            message = firstError || 'Validation error';
          } else {
            const fieldName = firstField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            message = `${fieldName}: ${firstError}`;
          }
        }
        // Case 2: DRF detail error
        // Format: { "detail": "Error message" }
        else if (data.detail) {
          const detailStr = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
          detail = detailStr;
          message = detailStr;
        }
        // Case 3: Custom message field
        // Format: { "message": "Error message" }
        else if (data.message) {
          message = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
        }
        // Case 4: Custom errors field (our format)
        // Format: { "errors": { "field": ["error"] }, "message": "..." }
        else if (data.errors && typeof data.errors === 'object') {
          errors = data.errors;
          message = data.message || 'Validation error';
        }
      }
      // Case 5: String response
      else if (typeof data === 'string') {
        message = data;
      }

      // Fallback to status-based messages if we still have generic message
      if (message === 'An error occurred') {
        switch (status) {
          case 400:
            message = 'Bad request - please check your input';
            break;
          case 401:
            message = 'Unauthorized - please log in';
            break;
          case 403:
            message = 'Forbidden - you do not have permission';
            break;
          case 404:
            message = 'Resource not found';
            break;
          case 422:
            message = 'Validation error - please check your input';
            break;
          case 500:
            message = 'Server error - please try again later';
            break;
          case 502:
            message = 'Bad gateway - server is unavailable';
            break;
          case 503:
            message = 'Service unavailable - please try again later';
            break;
        }
      }

      return {
        message,
        status,
        errors,
        detail,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: 'Network error. Please check your connection.',
        status: 0,
      };
    } else {
      // Error in request setup
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  }

  /**
   * Get authentication token from localStorage
   */
  private get_auth_token(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Get refresh token from localStorage
   */
  private get_refresh_token(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Set authentication token in localStorage
   */
  private set_auth_token(token: string): void {
    localStorage.setItem('access_token', token);
  }

  /**
   * Clear authentication tokens from localStorage
   */
  private clear_auth_tokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  /**
   * Refresh authentication token
   */
  private async refresh_auth_token(refresh_token: string): Promise<{ access: string }> {
    const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
      refresh: refresh_token,
    });
    return response.data;
  }

  /**
   * HTTP GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, config);
    return response.data;
  }

  /**
   * HTTP POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config);
    return response.data;
  }

  /**
   * HTTP PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data, config);
    return response.data;
  }

  /**
   * HTTP PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(url, data, config);
    return response.data;
  }

  /**
   * HTTP DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config);
    return response.data;
  }

  /**
   * Upload file with multipart/form-data
   */
  async upload<T = any>(url: string, form_data: FormData, on_progress?: (progress: number) => void): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progress_event) => {
        if (on_progress && progress_event.total) {
          const percent = Math.round((progress_event.loaded * 100) / progress_event.total);
          on_progress(percent);
        }
      },
    });
    return response.data;
  }

  /**
   * Download file as blob
   */
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.api.get(url, {
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response.data]);
    const download_url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = download_url;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(download_url);
  }
}

/**
 * Enhanced error handler for blob responses
 * Call this in catch blocks for blob requests
 */
export const handleBlobError = async (error: any): Promise<never> => {
  if (error?.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      
      // Try to parse as JSON
      try {
        const json_error = JSON.parse(text);
        const error_message = json_error.error || json_error.detail || json_error.message || 'Unknown error';
        
        // Create a more specific error
        const new_error: any = new Error(error_message);
        new_error.response = {
          ...error.response,
          data: json_error
        };
        throw new_error;
      } catch (parse_error) {
        // Not JSON, throw text as error
        throw new Error(text || 'PDF generation failed');
      }
    } catch (blob_error: any) {
      throw blob_error;
    }
  }
  
  throw error;
};


// Export singleton instance
export const api_service = new ApiService();

// Export type for use in other services
export type { ApiError };